const MIN_DAY = 1
const MAX_DAY = 31
const MIN_MONTH = 1
const MAX_MONTH = 12
const LEAP_YEAR = 2000

type DateInputRange = {
  minYear: number
  maxYear: number
}

type DateInputChange = {
  inputType: string
  previousValue: string
  selectionStart: number | null
}

type ParseDate = (value: string) => Date | undefined

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

const getMaxDay = (monthValue: string, yearValue: string) => {
  if (monthValue.length < 2) {
    return MAX_DAY
  }

  const month = Number(monthValue)

  if (month < 1 || month > 12) {
    return MAX_DAY
  }

  const year = yearValue.length === 4 ? Number(yearValue) : LEAP_YEAR
  const lastDayOfMonth = new Date(0)

  lastDayOfMonth.setFullYear(year, month, 0)

  return lastDayOfMonth.getDate()
}

const normalizeDay = (dayValue: string, maxDay: number) => {
  if (dayValue.length < 2) {
    return dayValue
  }

  const day = clamp(Number(dayValue), MIN_DAY, maxDay)

  return String(day).padStart(2, '0')
}

const normalizeMonth = (monthValue: string) => {
  if (monthValue.length < 2) {
    return monthValue
  }

  const month = clamp(Number(monthValue), MIN_MONTH, MAX_MONTH)

  return String(month).padStart(2, '0')
}

const normalizeYear = (yearValue: string, { minYear, maxYear }: DateInputRange) => {
  if (yearValue.length === 2) {
    const century = Math.floor(maxYear / 100) * 100
    const year = clamp(century + Number(yearValue), minYear, maxYear)

    return String(year).slice(-2)
  }

  if (yearValue.length < 4) {
    return yearValue
  }

  return String(clamp(Number(yearValue), minYear, maxYear))
}

export const formatDateInput = (value: string, range: DateInputRange) => {
  const digits = value.replace(/\D/g, '').slice(0, 8)
  const month = normalizeMonth(digits.slice(2, 4))
  const year = normalizeYear(digits.slice(4, 8), range)
  const day = normalizeDay(digits.slice(0, 2), getMaxDay(month, year))

  return [day, month, year].filter(Boolean).join('.')
}

const isManualDateSeparator = (
  value: string,
  { inputType, previousValue, selectionStart }: DateInputChange,
) =>
  inputType === 'insertText' &&
  selectionStart === value.length &&
  value === `${previousValue}.` &&
  /^\d{1,2}(?:\.\d{1,2})?$/.test(previousValue)

const canAutoFormat = (
  value: string,
  range: DateInputRange,
  { inputType, previousValue, selectionStart }: DateInputChange,
) => {
  const isInsertion = inputType.startsWith('insert')
  const isEditingInsideValue = selectionStart !== null && selectionStart < value.length
  const wasAutoFormatted = formatDateInput(previousValue, range) === previousValue

  return isInsertion && !isEditingInsideValue && wasAutoFormatted
}

export const getNextDateInputValue = (
  value: string,
  range: DateInputRange,
  change: DateInputChange,
) => {
  if (isManualDateSeparator(value, change)) {
    return value
  }

  return canAutoFormat(value, range, change) ? formatDateInput(value, range) : value
}

export const normalizeDateInputOnBlur = (value: string, parseDate: ParseDate) => {
  const dateParts = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/)

  if (!dateParts) {
    return value
  }

  const [, day, month, year] = dateParts
  const paddedValue = [day.padStart(2, '0'), month.padStart(2, '0'), year].join('.')
  const parsedDate = parseDate(paddedValue)

  if (!parsedDate) {
    return value
  }

  const normalizedYear = year.length === 2 ? String(parsedDate.getFullYear()) : year
  const normalizedValue = [
    day.padStart(2, '0'),
    month.padStart(2, '0'),
    normalizedYear,
  ].join('.')

  return parseDate(normalizedValue) ? normalizedValue : value
}
